import React, { useEffect, useRef } from 'react';

/**
 * SheetShell — the ONE overlay container for the CRM suite (design 01-architecture §4.2, corpus P28).
 *
 * Extracted from ValueSheet (the fixed original) so the backdrop, the shape switch and the dismiss
 * rules exist ONCE. GoalsBoard's editor was a line-for-line fork of this container and had already
 * diverged (no Escape, no desktop shape — 11-Goal D-G3); a fix landed in the original never reached
 * the copy. Rendering every overlay through this shell makes that fork unrepresentable, not merely
 * absent today — the structural check is that the z-[69] backdrop literal exists in exactly one file.
 *
 * Three shapes, chosen by the CALLER (this component never reads the breakpoint — same contract as
 * ValueSheet, because a shell embedded in a narrow pane must not consult the viewport):
 *   'sheet'   — bottom sheet (phone), backdrop, safe-area padding
 *   'popover' — anchored to `anchor`, no backdrop (table-cell editors)
 *   'center'  — centered modal card over a backdrop (desktop dialogs)
 *
 * Dismiss contract (all shapes): Escape closes; a pointer-down outside the shell closes. A click on
 * the shell's own content BUBBLES up through the backdrop — the body is its DOM child, not a sibling —
 * so the backdrop only closes when the click genuinely originated ON the backdrop itself (root-caused
 * by UXeng; engine-independent). Both rules live here and only here.
 */
export interface SheetShellProps {
  shape: 'sheet' | 'popover' | 'center';
  anchor?: HTMLElement | null; // popover only
  onClose: () => void;
  /** Forwarded onto the shell body as data-value-sheet so existing shape assertions keep working. */
  shapeTestAttr?: string;
  testId?: string;
  className?: string; // extra classes for the body (width overrides etc.)
  children: React.ReactNode;
}

export const SheetShell: React.FC<SheetShellProps> = ({ shape, anchor, onClose, shapeTestAttr, testId, className, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const bodyClass = shape === 'sheet'
    ? 'fixed inset-x-0 bottom-0 z-[70] bg-surface rounded-t-2xl border-t border-border-neutral shadow-xl max-h-[70vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]'
    : shape === 'popover'
      // FIXED, not absolute: the popover renders inside whatever pane opened it, and the console's
      // panes are overflow-hidden/auto scroll containers — an absolutely-positioned popover gets
      // CLIPPED by them (caught by goals_complete_loop the moment the goal editor became a popover).
      // position:fixed ignores ancestor overflow, and the viewport rect is already the coordinate
      // space popoverPos measures in.
      ? 'fixed z-[70] bg-surface rounded-xl border border-border-neutral shadow-lg max-h-80 overflow-y-auto'
      : 'z-[70] w-full max-w-sm mx-4 bg-surface rounded-2xl border border-border-neutral shadow-xl max-h-[80vh] overflow-y-auto';

  const body = (
    <div ref={ref} role={shape === 'popover' ? undefined : 'dialog'} aria-modal={shape === 'popover' ? undefined : true}
      data-value-sheet={shapeTestAttr ?? shape} data-testid={testId}
      className={`${bodyClass} ${className ?? ''}`}
      style={shape === 'popover' && anchor ? popoverPos(anchor) : undefined}
    >
      {children}
    </div>
  );

  if (shape === 'popover') return body;
  return (
    <div
      className={`fixed inset-0 z-[69] bg-black/30 ${shape === 'center' ? 'flex items-center justify-center' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {body}
    </div>
  );
};

// Anchor the popover just below the cell (viewport coordinates — the body is position:fixed, so
// getBoundingClientRect IS the right coordinate space; no scroll offsets), clamped to the viewport
// start-edge (logical inset for RTL).
function popoverPos(anchor: HTMLElement): React.CSSProperties {
  const r = anchor.getBoundingClientRect();
  // Clamp so the widest shell body (w-80 = 320px) stays inside the viewport — an anchor near the
  // end edge (e.g. the third goal card's Edit control) would otherwise push the popover off-screen.
  const start = Math.max(8, Math.min(r.left, window.innerWidth - 336));
  return { top: r.bottom + 4, insetInlineStart: start };
}
